import { useCallback, useEffect, useRef, useState } from 'react'

import {
  selectAiMode,
  setAiMode as setAiModeAction,
} from '@disco/state-management/reducers/search-page'

import type { AiMode } from 'types/search-page'

import { useDispatch, useSelector } from 'hooks/use-redux'

export type { AiMode }

export function useAiSearchToggle() {
  const dispatch = useDispatch()
  const aiMode = useSelector(selectAiMode)
  const [aiPrompt, setAiPrompt] = useState('')
  const rootRef = useRef<HTMLDivElement>(null)
  const promptRef = useRef(aiPrompt)

  const setAiMode = useCallback(
    (mode: AiMode) => {
      dispatch(setAiModeAction(mode))
    },
    [dispatch],
  )

  // Keep prompt ref in sync
  useEffect(() => {
    promptRef.current = aiPrompt
  }, [aiPrompt])

  // Close overlay but keep toggle active if there's text
  const closeOverlay = useCallback(() => {
    const hasText = promptRef.current.trim().length > 0
    setAiMode(hasText ? 'active' : 'off')
  }, [setAiMode])

  const clearPrompt = useCallback(() => {
    setAiPrompt('')
  }, [])

  useEffect(() => {
    if (aiMode !== 'overlay') return

    const handleClickOutside = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        closeOverlay()
      }
    }
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeOverlay()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [aiMode, closeOverlay])

  return {
    aiMode,
    aiPrompt,
    setAiMode,
    setAiPrompt,
    clearPrompt,
    closeOverlay,
    rootRef,
  }
}
